/**
 * Does the officer's Caller Info Bar (the strip ABOVE the live video feed in
 * VideoLayout) have anything to show?
 *
 * WHY THIS EXISTS (owner report 2026-09-15): the bar's visibility gate listed
 * name/phone/email/callReason but OMITTED `address`, while the bar itself renders
 * an address row. So a caller who had provided ONLY an address (e.g. they state
 * the address before their name — a normal transient during extraction) fell
 * through to the "Waiting for caller information…" placeholder, and the address
 * never appeared above the video — even though it showed on the Entities tab
 * (which has no such gate).
 *
 * The gate MUST cover every field the bar actually renders. Keeping it here as
 * one pure predicate makes that invariant testable and stops a rendered field
 * from silently missing the gate again.
 */
export interface CallerInfoBarFields {
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  callReason?: string;
}

export function callerInfoBarHasContent(
  caller: CallerInfoBarFields | null | undefined,
): boolean {
  if (!caller) return false;
  return Boolean(
    caller.name || caller.phone || caller.email || caller.callReason || caller.address,
  );
}

/**
 * SAVED CALLER RESTORE: the contract (finish of review-fix batch 9, 2026-10-26).
 *
 * The automatic read on caller join and the Retry button read the caller saved
 * for this call and restore it into the live caller.
 *
 * GUARANTEES
 *  1. It runs only while the live caller is AT MOST A NAME. A seat that carries
 *     only a participant name is written by the metadata effect as a user-entered
 *     caller; that alone no longer refuses the read (the defect: the saved phone,
 *     address and reason never reached the bar for such a caller).
 *  2. Any other live value (phone, callback, email, address, reason, DOB, notes,
 *     location) outranks the snapshot: the restore then writes NOTHING.
 *  3. It fills blank fields only (every saved field, so an empty bar still gets
 *     the whole record as before, AI extras included). A non-blank live value,
 *     the name included, is never replaced.
 *  4. It never writes the officer's note over an explicit clear (''), never takes
 *     the saved `userEntered` flag, and keeps the live id when there is one.
 *  5. The check and the write are one reducer step (`restoreSavedCaller`), so
 *     whatever landed before the read resolved is what the check sees.
 *
 * NOT GUARANTEED (known limitations, deliberately not chased):
 *  - The seat's display name stays even when the saved record has a fuller name.
 *  - A different person who joins the same call as a name-only seat would get the
 *    previous caller's saved details; an empty bar already did on staging.
 *  - A restored phone on a user-entered caller is kept over a later AI phone (the
 *    AI merge locks user-entered name and phone), as for intake-form phones.
 *  - After a restore, a later name-only seat does not replace the caller (same
 *    rule as after an intake form; see `hasDispatchedIntakeRef`).
 */
const RESTORE_BLOCKING_FIELDS = ['phone', 'callbackNumber', 'email', 'address', 'callReason', 'dob', 'notes'] as const;
// Never copied by the blank fill: provenance and stamps stay live; `note` and `id` have their own rules.
const RESTORE_NEVER_FILLED = new Set(['userEntered', 'lastUpdatedAt', 'note', 'id']);

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

export interface RestorableCaller extends CallerInfoBarFields {
  id?: string;
  callbackNumber?: string;
  dob?: string;
  notes?: string;
  note?: string;
  location?: unknown;
  userEntered?: boolean;
}

function liveCallerIsAtMostAName(current: RestorableCaller | null | undefined): boolean {
  if (!current) return true;
  return !current.location && RESTORE_BLOCKING_FIELDS.every(field => !current[field]);
}

/**
 * Is the saved read allowed to restore into the live caller? (Guarantees 1-2.)
 */
export function shouldApplyRetriedCaller(
  retried: CallerInfoBarFields | null | undefined,
  current: RestorableCaller | null | undefined,
): boolean {
  return Boolean(retried) && liveCallerIsAtMostAName(current);
}

/**
 * The live caller after the restore, or null when nothing may be written
 * (guarantees 1-4). Pure; the reducer `restoreSavedCaller` is its only writer.
 */
export function restoreSavedCallerInto<C extends RestorableCaller>(
  current: C,
  saved: RestorableCaller | null | undefined,
): C | null {
  if (!saved || !shouldApplyRetriedCaller(saved, current)) return null;
  const next: C = { ...current };
  const out = next as unknown as Record<string, unknown>;
  const live = current as unknown as Record<string, unknown>;
  let changed = false;
  // Blank fill over every saved field (bar fields, location, AI extras such as
  // discoveryQuestions): a non-blank live value is never replaced.
  for (const [field, value] of Object.entries(saved)) {
    if (RESTORE_NEVER_FILLED.has(field) || isBlank(value) || !isBlank(live[field])) continue;
    out[field] = value;
    changed = true;
  }
  if (current.note === undefined && saved.note) {
    out.note = saved.note;
    changed = true;
  }
  if (!current.id && saved.id) {
    out.id = saved.id;
    changed = true;
  }
  return changed ? next : null;
}

export interface CallerInfoBarLocation {
  address?: string;
  name?: string;
  locationType?: string;
}

/**
 * The caller-STATED address to show in the bar (owner 2026-09-16, "location
 * still does not show above the live video").
 *
 * The address a caller gives is usually the INCIDENT scene, extracted as a
 * Location entity (`locationType: 'incident_scene'`), NOT onto `caller.address`
 * — which only holds a caller's OWN residence address, rarely spoken. The bar
 * reads only `caller.*`, so it never surfaced the incident address even though
 * the Entities tab did. This mirrors the Entities tab's own derivation: prefer
 * the incident scene, else the first location; prefer its address, else its
 * name. Returns undefined when there is nothing to show, so the caller's own
 * `caller.address` (when present) is never duplicated by this.
 */
export type IncidentAddress = {
  text: string;
  /**
   * Where the text came from (#1341): an `incident_scene` location is the
   * address the caller STATED; any other location is merely a place that came
   * up in the call — a residence, a workplace, a landmark — and the bar must
   * say so instead of presenting it under the address pin as if it were the
   * address. Never guessed from position in the list: only the entity's own
   * `locationType` decides.
   */
  source: 'incident_scene' | 'mentioned';
};

export function incidentAddressWithSource(
  locations: readonly CallerInfoBarLocation[] | null | undefined,
): IncidentAddress | undefined {
  if (!locations || locations.length === 0) return undefined;
  const scene = locations.find((loc) => loc.locationType === 'incident_scene');
  const primary = scene ?? locations[0];
  const text = primary?.address || primary?.name || undefined;
  if (!text) return undefined;
  return { text, source: scene ? 'incident_scene' : 'mentioned' };
}

/** The label the bar shows beside a location that is NOT the stated address. */
export const MENTIONED_LOCATION_LABEL = 'Location mentioned';

export function incidentAddressFor(
  locations: readonly CallerInfoBarLocation[] | null | undefined,
): string | undefined {
  return incidentAddressWithSource(locations)?.text;
}

/**
 * Which empty state the Caller Info Bar shows (owner 2026-10-24: "too vertical",
 * "retry button is pointless when no caller has joined").
 *
 *   no-caller    nobody has joined: nothing to retry.
 *   joined-empty a caller is here, the read worked, nothing has been shared yet:
 *                details arrive on their own, nothing to retry.
 *   failed       a real failed read or a timeout: the only state that offers Retry.
 */
export type CallerInfoEmptyKind = 'no-caller' | 'joined-empty' | 'failed';

export function callerInfoEmptyKind(input: {
  callerJoined: boolean;
  error?: string | null;
}): CallerInfoEmptyKind {
  if (input.error) return 'failed';
  return input.callerJoined ? 'joined-empty' : 'no-caller';
}

/**
 * The Caller Info Bar's container classes. The 112px minimum height belongs to a
 * bar that has details to lay out; an empty bar is one compact row (owner
 * 2026-10-24: "too vertical"). Shared by VideoLayout and the review harness.
 */
export function callerInfoBarContainerClass(hasContent: boolean): string {
  return `bg-chrome border-b border-dark-border px-3 py-2 z-10 max-h-[240px] overflow-y-auto relative${hasContent ? ' min-h-[112px]' : ''}`;
}

/**
 * The one caller-info read used by both the automatic read on caller join and
 * the Retry button: a saved caller populates the bar with no click, unless a
 * live form or officer entry that arrived DURING the read outranks it (the
 * current caller is read after the read resolves, on purpose).
 */
export async function readCallerInfoInto<T extends CallerInfoBarFields>(
  read: () => Promise<T | null>,
  current: () => RestorableCaller | null | undefined,
  apply: (caller: T) => void,
): Promise<void> {
  const caller = await read();
  if (caller && shouldApplyRetriedCaller(caller, current())) apply(caller);
}
